import { DataModelFieldType } from 'constants/global';
import { MASKS } from 'constants/masks';
import { FilterOperation } from 'container/search';
import { string } from 'utils/normalizers';
import { isRequired } from 'utils/validators';

const TEXTS = {
  LABEL: 'Select Field to Search',
  FIELDS: {
    FIELD: { label: 'Field', placeholder: 'Fields', defaultValue: '' },
    FUNCTION: { label: 'Function', placeHolder: 'Functions', defaultValue: '' },
    VALUE: { label: 'Value', placeholder: 'Value', defaultValue: '' },
  },
  ADD_FIELD: 'Add a Field',
  REJECT: 'Reset',
  SUBMIT: 'Apply',
  SAVE_BTN: 'Save Search',
  CANCEL: 'Cancel',
  SAVE_SEARCH_TEMPLATE_MODAL: {
    LABEL: 'Save Search',
    TOAST_MSG: 'Successfully saved search',
  },
};

const FIELDS = {
  OPERATOR: {
    name: 'operator',
  },
  ARRAY: {
    name: 'fieldFunctionValueInput',
  },
  FIELD: {
    name: 'field',
    label: TEXTS.FIELDS.FIELD.label,
    placeholder: TEXTS.FIELDS.FIELD.placeholder,
    validate: isRequired,
  },
  FUNCTION: {
    name: 'function',
    label: TEXTS.FIELDS.FUNCTION.label,
    placeholder: TEXTS.FIELDS.FUNCTION.placeHolder,
    validate: isRequired,
  },
  VALUE: {
    name: 'value',
    label: TEXTS.FIELDS.VALUE.label,
    placeholder: TEXTS.FIELDS.VALUE.placeholder,
  },
  NUMBER_VALUE: {
    placeholder: '0',
    normalize: string.getOnlyNumbers,
    format: string.getOnlyNumbers,
  },
  DATE_VALUE: {
    name: 'value',
    label: TEXTS.FIELDS.VALUE,
    mask: MASKS[DataModelFieldType.Date],
    placeholder: 'mm/dd/yyyy',
  },
};

export const atf_map: Partial<
  Record<AdvancedSearchOperation, FilterOperation>
> = {
  contains: 'contains',
  end_with: 'end_with',
  equal: 'equal',
  greater_than: 'greater',
  gte: 'greater_or_equal',
  is_null: 'is_null',
  less_than: 'less',
  lte: 'less_or_equal',
  starts_with: 'starts_with',
  // no equivelant for ASO 'is_empty'
  // no equivelant for FO 'in'
};

export const fta_map: Partial<
  Record<FilterOperation, AdvancedSearchOperation>
> = Object.fromEntries(Object.entries(atf_map).map(([a, f]) => [f, a]));

export interface AdvancedSearchFunction {
  label: string;
  value: string;
  type: 'number' | 'string';
  config: {
    operation: AdvancedSearchOperation;
    not?: boolean;
  };
}

const NUMBER_FUNCTIONS: Record<string, AdvancedSearchFunction> = {
  equal: {
    label: 'Equal',
    value: 'equal',
    type: 'number',
    config: {
      operation: 'equal',
    },
  },
  equalNot: {
    label: 'Not equal',
    value: 'equalNot',
    type: 'number',
    config: {
      operation: 'equal',
      not: true,
    },
  },
  greaterThan: {
    label: 'Greater than',
    value: 'greaterThan',
    type: 'number',
    config: {
      operation: 'greater_than',
    },
  },
  lessThan: {
    label: 'Less than',
    value: 'lessThan',
    type: 'number',
    config: {
      operation: 'less_than',
    },
  },
  gte: {
    label: 'Greater than or equal',
    value: 'gte',
    type: 'number',
    config: {
      operation: 'gte',
    },
  },
  lte: {
    label: 'Less than or equal',
    value: 'lte',
    type: 'number',
    config: {
      operation: 'lte',
    },
  },
  isNull: {
    label: 'Is Null',
    value: 'isNull',
    type: 'number',
    config: {
      operation: 'is_null',
    },
  },
  isNullNot: {
    label: 'Is not Null',
    value: 'isNullNot',
    type: 'number',
    config: {
      operation: 'is_null',
      not: true,
    },
  },
} as const;

const STRING_FUNCTIONS: Record<string, AdvancedSearchFunction> = {
  startsWith: {
    label: 'Start with',
    value: 'startsWith',
    type: 'string',
    config: {
      operation: 'starts_with',
    },
  },
  endWith: {
    label: 'End with',
    value: 'endWith',
    type: 'string',
    config: {
      operation: 'end_with',
    },
  },
  contains: {
    label: 'Contains',
    value: 'contains',
    type: 'string',
    config: {
      operation: 'contains',
    },
  },
  equal: {
    label: 'Equal',
    value: 'equal',
    type: 'string',
    config: {
      operation: 'equal',
    },
  },
  equalNot: {
    label: 'Not equal',
    value: 'equalNot',
    type: 'string',
    config: {
      operation: 'equal',
      not: true,
    },
  },
  isNull: {
    label: 'Is Null',
    value: 'isNull',
    type: 'string',
    config: {
      operation: 'is_null',
    },
  },
  isNullNot: {
    label: 'Is not Null',
    value: 'isNullNot',
    type: 'string',
    config: {
      operation: 'is_null',
      not: true,
    },
  },
} as const;

const OPERATOR_STATES = {
  left: { label: 'AND', value: 'AND' },
  right: { label: 'OR', value: 'OR' },
};

export { FIELDS, TEXTS, NUMBER_FUNCTIONS, STRING_FUNCTIONS, OPERATOR_STATES };
